import { Gift } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { btnPrimary, focusRing } from '../ui/redesign';

const WAVE =
  'M0,50L80,53.3C160,57,320,63,480,60C640,57,800,43,960,40C1120,37,1280,43,1360,46.7L1440,50L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z';

const WHY = [
  { key: 'stage', img: '/activities/IMG_1998.jpg' },
  { key: 'culture', img: '/activities/IMG_3058.jpg' },
  { key: 'safety', img: '/activities/IMG_2665.jpg' },
  { key: 'trust', img: '/activities/IMG_3208 2.jpg' },
] as const;

/** "为什么选择友友会？" in the live site's design: green band between two waves. */
export function WhySection() {
  const { t } = useTranslation('home');
  return (
    <>
      <svg
        className="relative z-[1] -mb-px mt-8 block h-auto w-full desk:mt-10"
        viewBox="0 0 1440 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path d={WAVE} fill="#3A7D44" />
      </svg>
      <section className="relative bg-primary pb-[52px] pt-5 text-white desk:pb-[90px] desk:pt-12">
        <div className="mx-auto max-w-[1160px] px-4 desk:px-7">
          <h2 className="mb-5 text-center font-ui text-[22px] font-bold leading-[1.6] text-white desk:mb-11 desk:text-[32px] [text-wrap:balance]">
            {t('whyChoose.title')}
          </h2>
          <div className="grid grid-cols-2 gap-x-3 gap-y-[22px] desk:gap-8 min-[1001px]:grid-cols-4">
            {WHY.map(({ key, img }) => (
              <div key={key} className="text-center">
                <div className="group mx-auto mb-2 aspect-[3/2] w-2/3 overflow-hidden rounded-lg shadow-[0_10px_15px_-3px_rgba(0,0,0,0.2),0_4px_6px_-4px_rgba(0,0,0,0.2)] desk:mb-3">
                  <img
                    src={img}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none"
                  />
                </div>
                <h3 className="mb-0.5 font-ui text-base font-semibold leading-[1.6] text-warm-white desk:mb-1 desk:text-[20px]">
                  {t(`whyChoose.${key}.title`)}
                </h3>
                <p className="text-[12.5px] leading-[1.45] text-warm-white/90 desk:text-sm desk:leading-[1.6]">
                  {t(`whyChoose.${key}.subtitle`)}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -bottom-px left-0 right-0">
          <svg
            className="block h-auto w-full"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d={WAVE} fill="#FAF6F1" />
          </svg>
        </div>
      </section>
    </>
  );
}

/** Parent quote with "阅读更多" when it runs past four lines. */
function TestimonialCard({ quote, author }: { quote: string; author: string }) {
  const { t } = useTranslation('home');
  const [open, setOpen] = useState(false);
  const [clamps, setClamps] = useState(false);
  const [el, setEl] = useState<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (!el) return;
    // Phones show three lines (compact), desktop four. Measured again once the web
    // font has loaded and on resize, since the fallback font wraps differently.
    const measure = () => {
      const lines = window.matchMedia('(min-width: 761px)').matches ? 4 : 3;
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      setClamps(el.scrollHeight > lineHeight * lines + 1);
    };
    measure();
    let live = true;
    void document.fonts?.ready.then(() => live && measure());
    window.addEventListener('resize', measure);
    return () => {
      live = false;
      window.removeEventListener('resize', measure);
    };
  }, [el, quote]);

  return (
    <div className="flex flex-[0_0_84%] snap-start flex-col rounded-xl bg-white p-6 shadow-brand desk:flex-auto">
      <p
        ref={setEl}
        className={`mb-4 text-sm leading-[1.625] text-ink/80 ${clamps && !open ? 'line-clamp-3 desk:line-clamp-4' : ''}`}
      >
        “{quote}”
      </p>
      {clamps && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`mb-4 self-start text-[12px] font-medium text-primary ${focusRing}`}
        >
          {open ? t('testimonials.readLess') : t('testimonials.readMore')}
        </button>
      )}
      <p className="mt-auto font-semibold">{author}</p>
    </div>
  );
}

function FounderQuote({
  img,
  name,
  role,
  quote,
  flip = false,
}: {
  img: string;
  name: string;
  role: string;
  quote: string;
  flip?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3 desk:items-center desk:gap-4 ${flip ? 'flex-row-reverse' : ''}`}
    >
      {/* Phones get a compact column: smaller avatar and type, the role may wrap. */}
      <div className="flex w-[84px] flex-none flex-col items-center text-center desk:w-32">
        <img
          src={img}
          alt=""
          className="mb-1.5 h-12 w-12 flex-none rounded-full bg-white object-cover shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] desk:mb-2 desk:h-16 desk:w-16"
        />
        <b className="font-ui text-[14px] font-semibold leading-[1.3] desk:text-[15px]">{name}</b>
        <span className="text-[11.5px] leading-[1.35] text-mute desk:whitespace-nowrap desk:text-[12.5px] desk:leading-normal">
          {role}
        </span>
      </div>
      <div className={`min-w-0 flex-1 ${flip ? 'text-right' : ''}`}>
        <p className="text-[14px] italic leading-[1.6] text-ink/90 desk:text-base desk:leading-[1.625]">
          “{quote}”
        </p>
      </div>
    </div>
  );
}

/** "深受友友家庭喜爱" (anchor #about): parent quotes and the founders. */
export function Testimonials() {
  const { t } = useTranslation('home');
  return (
    <section id="about" className="scroll-mt-[70px] pb-10 pt-[34px] desk:py-16">
      <div className="mx-auto max-w-[1160px] px-4 desk:px-7">
        <h2 className="mb-5 text-center font-ui text-[22px] font-bold leading-[1.6] text-ink desk:mb-4 desk:text-[32px] [text-wrap:balance]">
          {t('testimonials.title')}
        </h2>
        <div className="-mx-4 flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto scroll-px-4 px-4 pb-3 pt-1 [scrollbar-width:none] desk:mx-0 desk:grid desk:grid-cols-3 desk:gap-8 desk:overflow-visible desk:p-0 [&::-webkit-scrollbar]:hidden">
          <TestimonialCard
            quote={t('testimonials.testimonial1.quote')}
            author={t('testimonials.testimonial1.author')}
          />
          <TestimonialCard
            quote={t('testimonials.testimonial2.quote')}
            author={t('testimonials.testimonial2.author')}
          />
          <TestimonialCard
            quote={t('testimonials.testimonial3.quote')}
            author={t('testimonials.testimonial3.author')}
          />
        </div>
        <div className="mt-5 grid gap-5 rounded-2xl bg-primary-pale px-4 py-5 desk:mt-12 desk:gap-8 desk:p-10">
          <FounderQuote
            img="/founder-panda.png"
            name={t('testimonials.founder1.name')}
            role={t('testimonials.founder1.role')}
            quote={t('testimonials.founder1.quote')}
          />
          <FounderQuote
            img="/founder-susan.png"
            name={t('testimonials.founder2.name')}
            role={t('testimonials.founder2.role')}
            quote={t('testimonials.founder2.quote')}
            flip
          />
        </div>
      </div>
    </section>
  );
}

/** Gift card strip, the live design; one line on phones. */
export function GiftStrip() {
  const { t } = useTranslation('giftCard');
  return (
    <section className="mx-auto flex max-w-[1160px] justify-center px-4 pb-10 desk:px-7">
      <Link
        to="/gift-cards"
        className={`inline-flex w-full max-w-2xl flex-nowrap items-center justify-start gap-2.5 rounded-2xl border border-line bg-white px-3 py-2.5 shadow-brand transition-colors hover:border-primary desk:w-auto desk:flex-wrap desk:justify-center desk:gap-6 desk:px-6 desk:py-3 ${focusRing}`}
      >
        <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-primary/10 text-primary">
          <Gift className="h-5 w-5" aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 text-sm font-medium desk:flex-none desk:text-base">
          {t('giftCard:homeBanner.headline')}
        </p>
        <span className="inline-flex whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-[13px] font-semibold text-white desk:px-4 desk:text-sm">
          {t('giftCard:homeBanner.cta')}
        </span>
      </Link>
    </section>
  );
}

/** "把你的活动带到友友会" (PRD §3.1 #7); the explanation line is desktop only. */
export function HostCta() {
  const { t } = useTranslation('home');
  return (
    <section className="mx-auto max-w-[1160px] px-4 pb-9 desk:px-7 desk:pb-14">
      <div className="flex flex-wrap items-center justify-between gap-[22px] rounded-3xl border border-line bg-white p-5 desk:px-[30px] desk:py-[26px]">
        <div>
          <div className="font-ui text-[12.5px] font-semibold uppercase tracking-[0.08em] text-primary">
            {t('hostCta.eyebrow')}
          </div>
          <h2 className="mt-1.5 font-ui text-[clamp(20px,2.2vw,26px)] font-bold leading-[1.6] text-ink [text-wrap:balance]">
            {t('hostCta.title')}
          </h2>
          <p className="mt-2 max-w-[44em] text-[14.5px] font-bold text-primary-dark">
            {t('hostCta.stats')}
          </p>
          <p className="mt-1.5 hidden max-w-[44em] text-[14.5px] text-ink-2 desk:block">
            {t('hostCta.body')}
          </p>
        </div>
        <Link to="/apply-host" className={`${btnPrimary} w-full desk:w-auto`}>
          {t('hostCta.button')}
        </Link>
      </div>
    </section>
  );
}
